import { Suspense, useCallback, useEffect } from "react"
import { useQueryLoader } from "react-relay"
import { useDebounce } from "@uidotdev/usehooks"
import { AppointmentFilters } from "@/modules/waiting-room/components/appointment-filters/appointment-filters"
import { AppointmentSearch } from "@/modules/waiting-room/components/appointment-filters/appointment-search"
import { AppointmentWarning } from "@/modules/waiting-room/components/appointment-table/appointment-warning"
import { AppointmentsTable } from "@/modules/waiting-room/components/appointment-table/appointments-table"
import { HasMoreDataWarning } from "@/modules/waiting-room/components/appointment-table/has-more-data-warning"
import { StatusFilterWarning } from "@/modules/waiting-room/components/appointment-table/status-filter-warning"
import { UrgentAcuteAppointmentWarning } from "@/modules/waiting-room/components/appointment-table/urgent-acute-appointment-warning"
import { ShareMedicalRecordWarnings } from "@/modules/waiting-room/components/share-medical-record-warnings"
import { useWaitingRoomFilters } from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { WaitingRoomAppointmentsQuery } from "@/modules/waiting-room/services/__generated__/WaitingRoomAppointmentsQuery.graphql"
import { useSlotUpdatedSubscription } from "@/modules/waiting-room/services/useSlotUpdatedSubscription"
import { fetchWaitingRoomData, waitingRoomQuery } from "@/modules/waiting-room/services/WaitingRoomAppointmentsQuery"

export const Appointments = ({ ref }: { ref?: React.Ref<HTMLDivElement> }) => {
  const { filters, hasFilters } = useWaitingRoomFilters()

  const debouncedFilters = useDebounce(filters, 500)

  const [queryReference, loadQuery] = useQueryLoader<WaitingRoomAppointmentsQuery>(waitingRoomQuery)

  useEffect(() => {
    loadQuery({ filters: debouncedFilters }, { fetchPolicy: "network-only" })
  }, [debouncedFilters, loadQuery])

  const autoFetchWaitingRoom = useCallback(() => {
    if (debouncedFilters.dateRange) {
      console.log("Timer elapsed: automatic refresh of waiting room is disabled due to date filter")
      return
    }
    console.log("Timer elapsed: automatic refresh of waiting room")

    fetchWaitingRoomData(debouncedFilters)
  }, [debouncedFilters])

  useEffect(() => {
    const id = setInterval(() => autoFetchWaitingRoom(), 60000)

    return () => clearInterval(id)
  }, [autoFetchWaitingRoom])

  useSlotUpdatedSubscription(debouncedFilters)

  return (
    <div className="grid grid-rows-[auto_auto_1fr]">
      <div className="grid gap-4">
        <div className="grid grid-cols-[auto_minmax(300px,300px)] gap-4 p-8 pb-0">
          <AppointmentFilters />
          <AppointmentSearch />
        </div>
        <ShareMedicalRecordWarnings />
        <AppointmentWarning />
        <StatusFilterWarning />
        <Suspense>{queryReference && <HasMoreDataWarning queryReference={queryReference} />}</Suspense>
      </div>
      <Suspense>{queryReference && <UrgentAcuteAppointmentWarning queryReference={queryReference} />}</Suspense>
      <Suspense>
        <div
          ref={ref}
          className="appointments-table animate-fade-in-bottom text-on-surface relative z-0 grid h-full w-full scrollbar-track-transparent px-8 transition-all duration-300"
          data-testid="appointment-content">
          {queryReference && <AppointmentsTable queryReference={queryReference} hasFilters={hasFilters} />}
        </div>
      </Suspense>
    </div>
  )
}
